import {
  useEffect, useState
} from 'react'
import {
  areFlagsSettledForIdentifiedUser,
  type FeatureFlagName,
  getFeatureFlagVariantSync,
  haveFeatureFlagsLoaded,
  peekFeatureFlagVariant,
} from '../analytics/posthog'

export type ExperimentArm = 'test' | 'control'

export interface ExperimentAssignment {
  arm: ExperimentArm
  // Whether this person counts in the experiment. Only someone eligible whose
  // arm was known at first paint is enrolled; everyone else sees control,
  // unexposed.
  enrolled: boolean
}

/**
 * A two-arm experiment decided once, on the first render, and never waited
 * for: a missing flag, a slow PostHog or an outage degrades to control rather
 * than to a spinner. When the person is not `eligible`, or their own flags are
 * not in yet (no response this load, or the reload after a sign-in still
 * pending), the surface paints control and records no exposure, so a late
 * `test` cannot count a person who never saw it. Any value but `test` is
 * control. Use `useExperimentVariant` instead where waiting for the arm is
 * acceptable.
 */
export function useFirstPaintArm(flag: FeatureFlagName, eligible = true): ExperimentAssignment {
  const [assignment] = useState<ExperimentAssignment>(() => {
    if (!eligible || !haveFeatureFlagsLoaded() || !areFlagsSettledForIdentifiedUser()) {
      return { arm: 'control', enrolled: false }
    }
    const variant = peekFeatureFlagVariant(flag)
    return { arm: variant === 'test' ? 'test' : 'control', enrolled: true }
  })

  useEffect(() => {
    if (assignment.enrolled) getFeatureFlagVariantSync(flag)
  }, [assignment.enrolled, flag])

  return assignment
}
